import { Injectable } from '@angular/core';
import { JSONPath } from 'jsonpath-plus';
import { BehaviorSubject, Observable, filter, map, pairwise } from 'rxjs';

export enum StorageType {
  LOCAL_STORAGE = 'local',
  SESSION_STORAGE = 'session',
}

export enum StorageScope {
  preference = 'preference',
  scenarioId = 'scenarioId',
}

type StorageScopeType = keyof typeof StorageScope;

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private _localDataSub$ = new BehaviorSubject<Record<string, any>>({});
  private _sessionDataSub$ = new BehaviorSubject<Record<string, any>>({});

  private _localData$ = this._localDataSub$.asObservable();
  private _sessionData$ = this._sessionDataSub$.asObservable();

  constructor() {
    this._initData(StorageType.LOCAL_STORAGE);
    this._initData(StorageType.SESSION_STORAGE);
  }

  /**
   * Sets a value under the scope
   * @param type Storage type (local or session)
   * @param scope A group where the values should be added.
   * @param path JSON path in the group object where the value needs to be added.
   * @param value New value to be added to the scope.
   */
  setItem(type: StorageType, scope: StorageScopeType, path: string, value: unknown): void {
    const storage = type === StorageType.LOCAL_STORAGE ? localStorage : sessionStorage;
    const jsonString: string | null = storage.getItem(scope);

    let jsonData: Record<string, unknown> = {};

    if (jsonString) jsonData = JSON.parse(jsonString) as Record<string, unknown>;

    const updatedData = this._updateJson(jsonData, path, value);

    storage.setItem(scope, JSON.stringify(updatedData));

    const stateData: Record<string, unknown> = structuredClone(this._getData(type));
    stateData[scope] = updatedData;

    this._setData(type, stateData);
  }

  /**
   * Get a value under the scope
   * @param type Storage type (local or session)
   * @param scope A group where the values should be added.
   * @param path JSON path in the group object where the value needs to be added.
   * @returns The value in the scopes json path.
   */
  getItem(type: StorageType, scope: StorageScopeType, path: string): unknown {
    const storage = type === StorageType.LOCAL_STORAGE ? localStorage : sessionStorage;
    const jsonString = storage.getItem(scope);

    if (!jsonString) return null;

    const jsonData: Record<string, unknown> = JSON.parse(jsonString) as Record<string, unknown>;

    if (typeof jsonData !== 'object') return jsonData;

    return JSONPath({ json: jsonData, path: path, wrap: false });
  }

  /**
   * Observable to get a value under the scope when it changes
   * @param type Storage type (local or session)
   * @param scope A group where the values should be added.
   * @param path JSON path in the group object where the value needs to be added.
   * @returns The value in the scopes json path if it changes.
   */
  getItem$(type: StorageType, scope: StorageScopeType, path: string): Observable<unknown> {
    const obs$ = type === StorageType.LOCAL_STORAGE ? this._localData$ : this._sessionData$;
    return obs$.pipe(
      pairwise(),
      filter(([previous, current]) => {
        const previousPathValue: string = JSONPath({ json: previous, path: `${scope}.${path}`, wrap: false });
        const currentPathValue: string = JSONPath({ json: current, path: `${scope}.${path}`, wrap: false });
        return previousPathValue != currentPathValue;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      map(([previous, current]) => JSONPath({ json: current, path: `${scope}.${path}`, wrap: false }))
    );
  }

  /**
   * Clear all the values or reset storage.
   * Usually used on session log-out.
   * @param type Optional storage type to clear. If not provided, clears both.
   */
  clearAll(type?: StorageType): void {
    if (type) {
      this._clearOne(type);
    } else {
      this._clearOne(StorageType.LOCAL_STORAGE);
      this._clearOne(StorageType.SESSION_STORAGE);
    }
  }

  private _initData(type: StorageType) {
    const storage = type === StorageType.LOCAL_STORAGE ? localStorage : sessionStorage;
    const data: Record<string, unknown> = {};
    Object.keys(storage).forEach((key: string) => {
      // Only consider data added through this service
      if (!Object.keys(StorageScope).includes(key)) return;

      const text = storage.getItem(key);
      if (text) data[key] = JSON.parse(text);
    });

    this._setData(type, data);
  }

  private _clearOne(type: StorageType) {
    const storage = type === StorageType.LOCAL_STORAGE ? localStorage : sessionStorage;
    const stateData: Record<string, unknown> = structuredClone(this._getData(type));
    Object.keys(StorageScope).forEach((key: string) => {
      stateData[key] = null;
      storage.setItem(key, '');
    });
    this._setData(type, stateData);
  }

  /**
   * Updates a JSON object with a new value at the specified path.
   * @param json JSON object that needs to be updated.
   * @param jsonPath JSON path where new value should be updated
   * @param value New value to be added to the jsonpath
   * @returns Updated JSON object
   */
  private _updateJson(json: any, jsonPath: string, value: unknown): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    if (!jsonPath) return json;

    const pathArray = jsonPath.split('.');
    let current = json;

    for (let i = 0; i < pathArray.length; i++) {
      const key = pathArray[i];

      if (!current[key]) {
        // If the key doesn't exist, create an object
        current[key] = {};
      }

      if (i === pathArray.length - 1) {
        // If it's the last key, set the value
        current[key] = value;
      } else {
        // Otherwise, update 'current' to the nested object
        current = current[key];
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return json;
  }

  private _getData(type: StorageType): Record<string, unknown> {
    return type === StorageType.LOCAL_STORAGE ? this._localDataSub$.value : this._sessionDataSub$.value;
  }

  private _setData(type: StorageType, value: Record<string, unknown>) {
    if (type === StorageType.LOCAL_STORAGE) {
      this._localDataSub$.next(value);
    } else {
      this._sessionDataSub$.next(value);
    }
  }
}
